import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPartnerLead } from "../../redux/ReduxSlices/seoDataSlice";
import { FiLoader } from "react-icons/fi";
import { arrayToCsv } from "../../utils/toCvs";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

function PartnerLead() {
  const dispatch = useDispatch();

  const { getLead, isStatus, error } = useSelector(
    (state) => state.seoRegistration
  );

  useEffect(() => {
    if (isStatus === "idle") dispatch(fetchPartnerLead());
  }, [isStatus, dispatch]);

  if (isStatus === "loading") {
    console.log("loading");
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
        <FiLoader className="animate-spin text-[#822bda] text-4xl" />
      </div>
    );
  }

  if (isStatus === "failed") {
    return <p className="text-red-600 p-6">Could not fetch data: {error}</p>;
  }

  const handleDownload = () => {
    const csv = arrayToCsv(getLead);

    // create a Blob and a temporary link element
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `partner-Leads-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-md:px-4  rounded-lg bg-gradient-to-br from-white to-gray-50 max-md:mt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-black">Partners Leads</h2>

        {/* Download button */}
        <button
          onClick={handleDownload}
          className="bg-purple-500 hover:bg-purple-700 text-white py-2 px-2 text-sm rounded-md shadow"
        >
          Download CSV
        </button>
      </div>

      <div className="rounded-xl border border-purple-200 overflow-x-auto">
        <table
          className="min-w-full bg-white border border-purple-200 rounded-2xl"
          style={{ minWidth: "max-content" }}
        >
          <thead className="bg-violet-100 sticky top-0">
            <tr>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Sr No
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Full Name
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Email
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Company
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Mobile
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Country
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Aware investment
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Clients
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Reason
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Start Time
              </th>
              <th className="py-3 px-4 text-sm border-b border-purple-200 text-left text-black whitespace-nowrap">
                Created At
              </th>
            </tr>
          </thead>
          <tbody>
            {getLead.map((item, index) => (
              <tr
                key={`${item.id || index}-${index}`}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {index + 1}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.full_name}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.email}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.company_name === "" ? "-" : item.company_name}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.mobile_number?.trim() ? item.mobile_number : "-"}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.country?.trim() ? item.country : "-"}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.aware_of_investment ? "Yes" : "No"}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.has_clients}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.reason}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {item.start_time}
                </td>
                <td className="py-3 px-4 text-sm border-b border-purple-200 text-gray-700 whitespace-nowrap">
                  {(() => {
                    if (!item.created_at) return "-";
                    const timeZone =
                      Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const rawUTC = item.created_at.endsWith("Z")
                      ? item.created_at
                      : item.created_at.replace(" ", "T") + "Z";
                    const date = utcToZonedTime(new Date(rawUTC), timeZone);
                    return format(date, "dd MMM yyyy, hh:mm a");
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PartnerLead;
